/*----------------------------------
- TYPES
----------------------------------*/
import type Models from '@models/types';

export type TProspectStepError = Models.Notification_type;

// Modals
import { Modal as ConnectAccountModal } from '@/client/pages/clients/campaigns/settings/ConnectAccount';
//import FixInvalidLinkedinUrl from './LinkedinProfileNotFound';

declare const modal: { show: (component: typeof ConnectAccountModal) => void };

export default class ProspectStepError extends Error {
    constructor(
        public message: TProspectStepError,
        public detail?: string,
    ) {
        super(message);
    }
}

type TLeadInfos = { id: string; firstName: string | null; lastName: string | null };

/*----------------------------------
- ERROR MESSAGES
----------------------------------*/
export const errorMessages: {
    [code in TProspectStepError]?: {
        short: string;
        // Can't be shown
        // - Send via email after running erorr
        // - Show in UI after running error
        explanation: (lead?: TLeadInfos) => string;
        silent?: boolean;
        ctaText?: string;
        ctaUrl?: string;
        uiAction?: () => void;
    };
} = {
    credits_notEnough: {
        short: 'Credits quota exceeded',
        explanation: (lead?: TLeadInfos) =>
            `You don't have enough credits to prospect ${lead ? lead.firstName + ' ' + lead.lastName : 'the person'}.`,
        ctaText: 'Please buy more credits to resume prospecting.',
    },

    simpleLeads_notEnough: {
        short: 'Simple leads quota exceeded',
        explanation: (lead?: TLeadInfos) => `You reached the limit of simple leads for your plan.`,
        ctaText: 'Please upgrade your plan or contact us to increase your limit.',
    },
    enrichedLeads_notEnough: {
        short: 'Enriched leads quota exceeded',
        explanation: (lead?: TLeadInfos) => `You reached the limit of enriched leads for your plan.`,
        ctaText: 'Please upgrade your plan or contact us to increase your limit.',
    },
    linkedInMessages_notEnough: {
        short: 'LinkedIn messages quota exceeded',
        explanation: (lead?: TLeadInfos) => `You reached the limit of LinkedIn messages for your plan.`,
        ctaText: 'Please upgrade your plan or contact us to increase your limit.',
    },
    enrichementCredit_notEnough: {
        short: 'Enrichment credits quota exceeded',
        explanation: (lead?: TLeadInfos) => `You reached the limit of enrichment credits for your plan.`,
        ctaText: 'Please upgrade your plan or contact us to increase your limit.',
    },
    aiMessages_notEnough: {
        short: 'AI messages quota exceeded',
        explanation: (lead?: TLeadInfos) => `You reached the limit of AI messages for your plan.`,
        ctaText: 'Please upgrade your plan or contact us to increase your limit.',
    },

    /*----------------------------------
    - STEPS: GENERAL
    ----------------------------------*/
    // Enrichment revealed this lead is the same person as another lead already running
    // for this recruiter (the two were imported under slightly different identifiers).
    // Stopped so the person isn't messaged twice from two records.
    prospect_lead_duplicate: {
        short: 'Already being contacted under another record',
        silent: true,
        explanation: (lead?: TLeadInfos) =>
            `${lead ? lead.firstName + ' ' + lead.lastName : 'This person'} is already in one of your campaigns under a duplicate contact record, so this copy was stopped to avoid contacting them twice.`,
        ctaText: 'No action needed — the original contact keeps running.',
    },
    message_manual_empty: {
        short: "Can't send empty messages",
        explanation: (lead?: TLeadInfos) => `The message was not provided. Please provide it to continue.`,
    },

    /*----------------------------------
    - LINKEDIN
    ----------------------------------*/
    linkedin_account_notEnabled: {
        short: 'LinkedIn account not enabled',
        explanation: (lead?: TLeadInfos) =>
            `We can't prospect ${lead ? lead.firstName + ' ' + lead.lastName : 'the person'} because your LinkedIn account is not enabled.`,
        ctaUrl: '/dashboard/account-details#settings-billing',
        ctaText: 'Please upgrade your plan to send LinkedIn messages.',
    },
    linkedin_account_notConnected: {
        short: 'LinkedIn account has been disconnected',
        explanation: (lead?: TLeadInfos) =>
            `We can't prospect ${lead ? lead.firstName + ' ' + lead.lastName : 'the person'} because your LinkedIn account has been disconnected from Cross Path.`,
        ctaUrl: '/dashboard/google',
        ctaText: 'Please reconnect your LinkedIn account to resume prospecting.',
    },
    linkedin_profile_notFound: {
        short: 'LinkedIn profile not found',
        explanation: (lead?: TLeadInfos) =>
            `The LinkedIn profile of ${lead ? lead.firstName + ' ' + lead.lastName : 'the person'} was not found. It may be because this profile is hidden from the public, or the linkedin address you gave is incorrect.`,
        ctaText: 'Please check the URL and try again.',
        /*uiAction = (lead) => {
            modal.show(FixInvalidLinkedinUrl, { leadId: lead.id });
        }*/
    },
    // LinkedIn refuses the invitation for this specific person (declined a previous
    // invitation, or restricted who may invite them). Not a technical failure, and
    // retrying doesn't help — surfaced per-lead like whatsapp_recipient_notReachable.
    linkedin_invitation_notAllowed: {
        short: 'Cannot send a LinkedIn invitation to this person',
        silent: true,
        explanation: (lead?: TLeadInfos) =>
            `LinkedIn won't let you send a connection request to ${lead ? lead.firstName + ' ' + lead.lastName : 'the person'}. They may have declined a previous invitation, or they only accept invitations from people who know them.`,
        ctaText: 'Please try reaching them on another channel.',
    },

    /*----------------------------------
    - EMAIL
    ----------------------------------*/
    email_account_notEnabled: {
        short: 'Email sending not included in your plan',
        explanation: (lead?: TLeadInfos) =>
            `We can't prospect ${lead ? lead.firstName + ' ' + lead.lastName : 'the person'} because email sending is not included in your plan.`,
        ctaUrl: '/dashboard/account-details#settings-billing',
        ctaText: 'Please upgrade your plan to send emails.',
    },
    email_find_notEnabled: {
        short: 'Email finding not included in your plan',
        explanation: (lead?: TLeadInfos) =>
            `We can't prospect ${lead ? lead.firstName + ' ' + lead.lastName : 'the person'} because email finding is not included in your plan.`,
        ctaUrl: '/dashboard/account-details#settings-billing',
        ctaText: 'Please upgrade your plan to find emails.',
    },
    email_mailbox_notConnected: {
        short: "We can't send emails to your prospect",
        explanation: (lead?: TLeadInfos) =>
            `We can't prospect ${lead ? lead.firstName + ' ' + lead.lastName : 'the person'} because your Gmail account has been disconnected from Cross Path.`,
        ctaUrl: '/dashboard/google',
        ctaText: 'Please reconnect your Gmail account to resume prospecting.',
        uiAction: () => {
            modal.show(ConnectAccountModal);
        },
    },
    email_manual_subject: {
        short: "Can't send emails without a subject",
        explanation: (lead?: TLeadInfos) =>
            `You've confgurated a step to send emails, but you didn't provide a subject to this email. Emails should have a subject to be sent.`,
        ctaText: 'Please provide a subject to this email to resume prospecting.',
    },
    email_notFound: {
        short: 'No email found',
        silent: true,
        explanation: (lead?: TLeadInfos) =>
            `No email found for ${lead ? lead.firstName + ' ' + lead.lastName : 'the person.'}.`,
        ctaText: 'Please provide an email to this contact to resume prospecting.',
    },

    /*----------------------------------
    - WHATSAPP
    ----------------------------------*/
    whatsapp_account_notConnected: {
        short: 'WhatsApp account has been disconnected',
        explanation: (lead?: TLeadInfos) =>
            `We can't prospect ${lead ? lead.firstName + ' ' + lead.lastName : 'the person'} because your WhatsApp account has been disconnected from Cross Path.`,
        ctaUrl: '/dashboard/google',
        ctaText: 'Please reconnect your WhatsApp account to resume prospecting.',
    },
    whatsapp_account_notEnabled: {
        short: 'WhatsApp account not enabled',
        explanation: (lead?: TLeadInfos) =>
            `We can't prospect ${lead ? lead.firstName + ' ' + lead.lastName : 'the person'} because WhatsApp campaigns are not enabled in your pack.`,
        ctaUrl: '/dashboard/account-details#settings-billing',
        ctaText: 'Please update your pack to send WhatsApp messages.',
    },
    whatsapp_phone_notFound: {
        short: 'No phone found',
        silent: true,
        explanation: (lead?: TLeadInfos) =>
            `No phone number was found for ${lead ? lead.firstName + ' ' + lead.lastName : 'the person'}.`,
        ctaText: 'Please provide a valid international phone number to resume WhatsApp prospecting.',
    },
    whatsapp_phone_invalid: {
        short: 'Invalid WhatsApp phone number',
        silent: true,
        explanation: (lead?: TLeadInfos) =>
            `The phone number for ${lead ? lead.firstName + ' ' + lead.lastName : 'the person'} is not a valid international WhatsApp number.`,
        ctaText: 'Please provide the phone number with its country code.',
    },
    whatsapp_recipient_notReachable: {
        short: 'Contact is not reachable on WhatsApp',
        silent: true,
        explanation: (lead?: TLeadInfos) =>
            `${lead ? lead.firstName + ' ' + lead.lastName : 'The person'} could not be reached on WhatsApp with the available phone number.`,
        ctaText: 'Please check the phone number or use another channel.',
    },

    /*----------------------------------
    - PHONE
    ----------------------------------*/
    phone_find_notEnabled: {
        short: 'Phone finding not included in your plan',
        explanation: (lead?: TLeadInfos) =>
            `We can't prospect ${lead ? lead.firstName + ' ' + lead.lastName : 'the person'} because phone finding is not included in your plan.`,
        ctaUrl: '/dashboard/account-details#settings-billing',
        ctaText: 'Please upgrade your plan to find phones.',
    },
};
